<template>
  <div class="user-dashboard">
    <div class="page-head">
      <div>
        <div class="pill">我的使用</div>
        <h2>用户仪表盘</h2>
        <p class="sub">请求概览</p>
      </div>
      <div class="head-actions">
        <a-segmented v-model:value="range" :options="rangeOptions" @change="handleRangeChange" />
        <a-button type="primary" @click="refreshAll" :loading="loadingAny">刷新</a-button>
      </div>
    </div>

    <a-row :gutter="16" class="metric-row">
      <a-col :span="8">
        <a-card class="glass-card" :loading="quotaLoading" bordered>
          <div class="card-title">请求额度</div>
          <div class="quota-block">
            <div>
              <div class="quota-main">
                <span class="quota-value">{{ quotaLimitText }}</span>
                <span class="quota-label">总额度</span>
              </div>
              <div class="quota-sub">
                <span>已用：{{ quotaUsed }}</span>
                <span>剩余：{{ remainingText }}</span>
              </div>
            </div>
            <div class="quota-progress">
              <a-progress :percent="usagePercent" :status="quotaPercentStatus" />
              <div class="quota-tip">{{ quotaStatus }}</div>
            </div>
          </div>
          <div class="quota-meta">
            <span>刷新范围：{{ rangeLabel }}</span>
            <span v-if="lastRequestText">最近请求：{{ lastRequestText }}</span>
          </div>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card class="glass-card" :loading="logLoading" bordered>
          <div class="card-title">活跃时段</div>
          <div class="peak">
            <div class="peak-value">{{ peakHourText }}</div>
            <div class="peak-desc">根据{{ rangeLabel }}的个人请求计算</div>
            <div class="window">时间覆盖：{{ requestWindowText }}</div>
          </div>
          <div class="mini-chart">
            <div
              v-for="item in hourBars"
              :key="item.hour"
              class="mini-bar"
              :style="{ height: item.height + '%', background: item.isPeak ? 'linear-gradient(180deg, #69c0ff, #1890ff)' : '#e6f7ff' }"
              :title="`${item.hour}:00 - ${item.count}次`"
            ></div>
          </div>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card class="glass-card" bordered>
          <div class="card-title">账号信息</div>
          <div class="info-list">
            <div class="info-row">
              <span>统一用户 ID</span>
              <span class="mono">{{ authUserId || '未识别' }}</span>
            </div>
            <div class="info-row">
              <span>额度策略</span>
              <a-tag color="blue">{{ quotaLimit < 0 ? '不限额' : '配额控制' }}</a-tag>
            </div>
            <div class="info-row">
              <span>AI 能力</span>
              <a-tag :color="allowAi ? 'green' : 'default'">{{ allowAi ? '已开通' : '未开通' }}</a-tag>
            </div>
            <div class="info-row">
              <span>付费题库</span>
              <a-tag :color="allowPaid ? 'green' : 'default'">{{ allowPaid ? '可用' : '受限' }}</a-tag>
            </div>
            <div class="info-row tips">
              <span>提示</span>
              <span class="tip-text">额度将按请求自动刷新，若接近用完可在顶部申请或联系管理员。</span>
            </div>
          </div>
        </a-card>
      </a-col>
    </a-row>

    <a-row :gutter="16" class="metric-row">
      <a-col :span="10">
        <a-card class="glass-card" :loading="logLoading" bordered>
          <div class="card-title">请求概览</div>
          <div class="stat-block">
            <div>
              <div class="stat-label">区间内请求</div>
              <div class="stat-value">{{ totalInRange }}</div>
            </div>
            <div>
              <div class="stat-label">今日请求</div>
              <div class="stat-value accent">{{ todayCount }}</div>
            </div>
            <div>
              <div class="stat-label">命中率</div>
              <div class="stat-value">{{ successRateText }}</div>
            </div>
          </div>
          <div class="stat-foot">
            <span>数据范围：{{ rangeLabel }}</span>
            <span class="accent">仅展示个人请求</span>
          </div>
        </a-card>
      </a-col>
      <a-col :span="14">
        <a-card class="glass-card" :loading="logLoading" bordered>
          <div class="card-title">最近请求</div>
          <a-table
            size="small"
            :data-source="recentRequests"
            :columns="columns"
            :pagination="false"
            row-key="id"
            class="simple-table"
          >
            <template #createTime="{ text }">
              {{ formatDate(text) }}
            </template>
            <template #question="{ record }">
              <div class="question-cell" :title="record.question">
                {{ record.question || '-' }}
              </div>
            </template>
            <template #result="{ record }">
              <a-tag :color="record.hasAnswer ? 'green' : 'orange'">
                {{ record.hasAnswer ? '命中' : '未命中' }}
              </a-tag>
            </template>
          </a-table>
          <div v-if="!recentRequests.length" class="empty">暂无请求记录</div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { getQuota, getRequestLogs } from '@/api/TikuComponentApi'
import { hasGlobalAccess } from '@/utils/permissions'
import { asPageResult } from '@/utils/result'
import { useSessionStore } from '@/store/session'
import { storeToRefs } from 'pinia'

export default defineComponent({
  name: 'UserDashboard',
  setup() {
    const session = useSessionStore()
    const { authUserId, requestLimit: quotaLimit, requestUsed: quotaUsed, allowAi, allowPaid } = storeToRefs(session)
    const quotaLoading = ref(false)
    const logLoading = ref(false)
    const logs = ref([])
    const canViewGlobalLogs = hasGlobalAccess()
    const totalInRange = ref(0)
    const range = ref('7d')
    const rangeOptions = [
      { label: '24H', value: '24h' },
      { label: '7天', value: '7d' },
      { label: '14天', value: '14d' },
      { label: '全部', value: 'all' },
    ]
    const loadingAny = computed(() => quotaLoading.value || logLoading.value)
    const rangeLabel = computed(() => {
      const map = { '24h': '最近 24 小时', '7d': '最近 7 天', '14d': '最近 14 天', all: '全部' }
      return map[range.value] || '最近 7 天'
    })

    const remaining = computed(() => {
      if (quotaLimit.value < 0) return '不限'
      return Math.max(quotaLimit.value - quotaUsed.value, 0)
    })
    const quotaLimitText = computed(() => (quotaLimit.value < 0 ? '不限额' : quotaLimit.value))
    const remainingText = computed(() => (quotaLimit.value < 0 ? '不限' : remaining.value))
    const quotaPercentStatus = computed(() => (quotaLimit.value >= 0 && remaining.value <= quotaLimit.value * 0.1 ? 'exception' : 'normal'))
    const usagePercent = computed(() => {
      if (quotaLimit.value < 0 || quotaLimit.value === 0) return 0
      return Math.min(100, Math.round((quotaUsed.value / quotaLimit.value) * 100))
    })
    const quotaStatus = computed(() => {
      if (quotaLimit.value < 0) return '当前为不限额模式'
      if (remaining.value === 0) return '额度已用完，需申请新增额度'
      if (remaining.value <= quotaLimit.value * 0.1) return '额度接近用完，建议尽快申请'
      return '额度充足'
    })

    const buildStart = (val) => {
      const now = Date.now()
      switch (val) {
        case '24h':
          return new Date(now - 24 * 60 * 60 * 1000)
        case '7d':
          return new Date(now - 7 * 24 * 60 * 60 * 1000)
        case '14d':
          return new Date(now - 14 * 24 * 60 * 60 * 1000)
        default:
          return null
      }
    }

    const fetchQuota = () => {
      quotaLoading.value = true
      getQuota().then(res => {
        if (res?.status === 200) {
          const { requestLimit, requestUsed } = res.data || {}
          session.setQuota(requestLimit, requestUsed)
        }
      }).finally(() => {
        quotaLoading.value = false
      })
    }

    const fetchLogs = () => {
      logLoading.value = true
      const params = { page: 1, pageSize: 200 }
      if (canViewGlobalLogs) {
        params.scope = 'self'
      }
      const start = buildStart(range.value)
      if (start) params.start = start.toISOString()
      getRequestLogs(params).then(res => {
        if (res?.status === 200) {
          const pageResult = asPageResult(res.data, 200)
          logs.value = pageResult.data
          totalInRange.value = pageResult.total
        }
      }).finally(() => {
        logLoading.value = false
      })
    }

    const handleRangeChange = () => {
      fetchLogs()
    }

    const refreshAll = () => {
      fetchQuota()
      fetchLogs()
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '-'
      const Y = date.getFullYear()
      const M = String(date.getMonth() + 1).padStart(2, '0')
      const D = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${Y}-${M}-${D} ${h}:${m}:${s}`
    }

    const recentRequests = computed(() => {
      const sorted = [...(logs.value || [])].sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
      return sorted.slice(0, 8)
    })

    const hourBars = computed(() => {
      const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }))
      logs.value.forEach(log => {
        const d = new Date(log.createTime)
        if (!isNaN(d.getTime())) {
          hours[d.getHours()].count += 1
        }
      })
      const max = Math.max(...hours.map(h => h.count), 1)
      const peak = hours.reduce((p, c) => (c.count > p.count ? c : p), { hour: 0, count: 0 })
      return hours.map(h => ({
        ...h,
        height: Math.max(h.count / max * 100, 5),
        isPeak: h.hour === peak.hour && h.count > 0,
      }))
    })

    const peakHourText = computed(() => {
      const peak = hourBars.value.reduce((p, c) => (c.count > p.count ? c : p), { hour: 0, count: 0 })
      if (!peak.count) return '暂无数据'
      return `${peak.hour}:00 - ${peak.count} 次`
    })

    const requestWindowText = computed(() => {
      if (!logs.value.length) return '暂无数据'
      const times = logs.value
        .map(l => new Date(l.createTime))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => a - b)
      if (!times.length) return '暂无数据'
      return `${formatDate(times[0])} 至 ${formatDate(times[times.length - 1])}`
    })

    const lastRequestText = computed(() => {
      if (!logs.value.length) return ''
      const latest = [...logs.value].sort((a, b) => new Date(b.createTime) - new Date(a.createTime))[0]
      return formatDate(latest.createTime)
    })

    const todayCount = computed(() => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
      const end = start + 24 * 60 * 60 * 1000
      return logs.value.filter(log => {
        const t = new Date(log.createTime).getTime()
        return t >= start && t < end
      }).length
    })

    const successRateText = computed(() => {
      if (!logs.value.length) return '0%'
      const success = logs.value.filter(l => l.hasAnswer).length
      const percent = Math.round((success / logs.value.length) * 1000) / 10
      return `${percent}%`
    })

    const columns = [
      { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 180, slots: { customRender: 'createTime' } },
      { title: '问题', dataIndex: 'question', key: 'question', ellipsis: true, slots: { customRender: 'question' } },
      { title: '耗时(ms)', dataIndex: 'durationMs', key: 'durationMs', width: 100 },
      { title: '结果', dataIndex: 'hasAnswer', key: 'hasAnswer', width: 90, slots: { customRender: 'result' } },
    ]

    onMounted(() => {
      fetchQuota()
      fetchLogs()
    })

    return {
      range,
      rangeOptions,
      rangeLabel,
      quotaLimit,
      quotaUsed,
      authUserId,
      quotaLimitText,
      remainingText,
      quotaStatus,
      usagePercent,
      quotaPercentStatus,
      remaining,
      quotaLoading,
      logLoading,
      logs,
      totalInRange,
      todayCount,
      successRateText,
      peakHourText,
      requestWindowText,
      lastRequestText,
      hourBars,
      recentRequests,
      allowAi,
      allowPaid,
      columns,
      formatDate,
      handleRangeChange,
      refreshAll,
      loadingAny,
      fetchQuota,
      fetchLogs
    }
  }
})
</script>

<style scoped>
.user-dashboard {
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: #f0f2f5;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-head h2 {
  margin: 6px 0;
  font-size: 26px;
  font-weight: 700;
  color: #1f1f1f;
}

.sub {
  color: #666;
  max-width: 560px;
  line-height: 1.6;
}

.pill {
  display: inline-flex;
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metric-row {
  width: 100%;
}

.glass-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  color: #333;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
  height: 100%;
  transition: all 0.3s;
}

.glass-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.card-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
  color: #1f1f1f;
  display: flex;
  align-items: center;
}

.card-title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 16px;
  background: #1890ff;
  margin-right: 8px;
  border-radius: 2px;
}

.quota-block {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.quota-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.quota-value {
  font-size: 28px;
  font-weight: 700;
}

.quota-label {
  color: #888;
  font-size: 13px;
}

.quota-sub {
  display: flex;
  gap: 16px;
  color: #666;
  margin-top: 8px;
}

.quota-progress {
  min-width: 180px;
}

.quota-tip {
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 6px;
}

.quota-meta {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  color: #8c8c8c;
  font-size: 12px;
}

.peak {
  margin-top: 8px;
  margin-bottom: 24px;
}

.peak-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f1f1f;
}

.peak-desc {
  color: #8c8c8c;
  margin-top: 4px;
}

.window {
  color: #555;
  margin-top: 4px;
  font-size: 13px;
}

.mini-chart {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 4px;
  align-items: flex-end;
  height: 100px;
  margin-top: 16px;
}

.mini-bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  transition: all 0.2s;
}

.mini-bar:hover {
  transform: scaleY(1.08);
  transform-origin: bottom;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #444;
}

.info-row.tips {
  align-items: flex-start;
}

.tip-text {
  max-width: 220px;
  text-align: right;
  color: #666;
  line-height: 1.5;
  font-size: 13px;
}

.mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  background: #f7f7f7;
  padding: 2px 8px;
  border-radius: 6px;
}

.stat-block {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
}

.stat-label {
  color: #8c8c8c;
  font-size: 13px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f1f1f;
}

.accent {
  color: #1890ff;
}

.stat-foot {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  color: #8c8c8c;
  font-size: 13px;
}

.simple-table :deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
}

.simple-table :deep(.ant-table-tbody > tr:hover > td) {
  background: #f0f7ff !important;
}

.question-cell {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
}

.empty {
  text-align: center;
  color: #ccc;
  padding: 16px 0;
}

@media (max-width: 992px) {
  .page-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .mini-chart {
    grid-template-columns: repeat(12, 1fr);
  }
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
